import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { 
  ChefHat, 
  Info,
  CheckCircle2,
  Wheat,
  Drumstick,
  Carrot,
  Droplet
} from 'lucide-react';

interface WHOGuideModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const stars = [
  {
    title: "Bintang 1: Karbohidrat",
    icon: <Wheat className="h-5 w-5 text-amber-600" />,
    description: "Sumber energi utama untuk tumbuh kembang anak.",
    examples: "Nasi, kentang, ubi, jagung, roti, pasta.",
    color: "bg-amber-50 border-amber-100 text-amber-900"
  },
  {
    title: "Bintang 2: Protein Hewani",
    icon: <Drumstick className="h-5 w-5 text-red-600" />,
    description: "Bahan pembangun tubuh dan otak, mencegah stunting.",
    examples: "Ayam, daging sapi, ikan, telur, hati ayam.",
    color: "bg-red-50 border-red-100 text-red-900"
  },
  {
    title: "Bintang 3: Protein Nabati",
    icon: <Carrot className="h-5 w-5 text-emerald-600" />,
    description: "Sumber serat dan mineral penting lainnya.",
    examples: "Tahu, tempe, kacang merah, kacang polong.",
    color: "bg-emerald-50 border-emerald-100 text-emerald-900"
  },
  {
    title: "Bintang 4: Sayur & Buah",
    icon: <Carrot className="h-5 w-5 text-green-600" />,
    description: "Vitamin dan mineral (pengenalan saja, porsi kecil).",
    examples: "Bayam, wortel, brokoli, pisang, pepaya.",
    color: "bg-green-50 border-green-100 text-green-900"
  }
];

export default function WHOGuideModal({ isOpen, onOpenChange }: WHOGuideModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] rounded-3xl p-0 overflow-hidden border-none shadow-2xl">
        <div className="bg-blue-600 p-6 text-white text-center space-y-2 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 relative z-10">
            <ChefHat className="h-8 w-8" />
          </div>
          <DialogTitle className="text-xl font-bold relative z-10">Menu 4⭐ Sesuai WHO/IDAI</DialogTitle>
          <DialogDescription className="text-white/80 text-xs relative z-10">
            Panduan nutrisi harian untuk menjamin kebutuhan tumbuh kembang optimal Si Kecil.
          </DialogDescription>
        </div>

        <ScrollArea className="p-6 max-h-[60vh]">
          <div className="space-y-6">
            <p className="text-sm font-medium text-stone-600 text-center px-4 leading-relaxed">
              Memastikan keempat komponen ini ada dalam piring makan Adek membantu mencegah stunting dan mendukung kecerdasan.
            </p>

            <div className="space-y-3">
              {stars.map((star, i) => (
                <div key={i} className={`p-4 rounded-2xl border ${star.color} flex gap-4`}>
                  <div className="bg-white/50 p-2 rounded-xl h-fit">
                    {star.icon}
                  </div>
                  <div className="space-y-1 mt-0.5">
                    <h4 className="font-bold text-sm tracking-tight">{star.title}</h4>
                    <p className="text-xs opacity-80 leading-relaxed font-medium">{star.description}</p>
                    <p className="text-[10px] opacity-60 leading-relaxed pt-1"><strong>Contoh:</strong> {star.examples}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 items-start">
               <Droplet className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
               <div className="space-y-1">
                 <p className="text-xs font-bold text-blue-900">Tambahan Penting: Lemak!</p>
                 <p className="text-[10px] text-blue-800/80 leading-relaxed font-medium">
                   Walaupun disebut "4 Bintang", pastikan selalu menambahkan sumber lemak tambahan 
                   seperti minyak ayam, mentega, santan, atau minyak canola pada setiap porsi makan.
                 </p>
               </div>
            </div>

            <div className="bg-stone-900 rounded-2xl p-4 flex gap-3 items-start">
               <Info className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
               <p className="text-[10px] text-white/80 leading-relaxed">
                 Jangan lupa selalu memantau grafik tinggi badan, berat badan, serta lingkar kepala anak setiap bulannya di KMS/Buku KIA.
               </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
